import React, { useEffect, useMemo } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import * as Sentry from '@sentry/react'

import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Button } from '../../../components/Button/Button'
import { ExportConfirmationConfig } from '../../../models/Account'

interface ExportConfirmationSettingsFormValues {
  message: string
}

const defaultValues: ExportConfirmationSettingsFormValues = { message: '' }

export const ExportConfirmationSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const [updateAccount, { isLoading: accountUpdating }] = useUpdateAccountMutation()

  const { hasQuota } = useZocksQuota()
  const exportConfirmationEnabled = hasQuota('integration.exConfirmEnabled.value')

  const form = useForm<ExportConfirmationSettingsFormValues>({ defaultValues })

  const { integrationEnabled, documentEnabled } = account?.data.effectiveConfig.complianceConfig.exportConfirmation ?? {}

  const exportConfirmationIsEnforcedByEnterpriseConfig = useMemo<boolean>(() => !!account?.data.enterpriseConfig.complianceConfig.exportConfirmation, [account])

  useEffect(() => {
    const { message } = account?.data.effectiveConfig.complianceConfig.exportConfirmation ?? {}
    if (!message) {
      return
    }

    form.reset({ message })
  }, [account, form])

  const updateExportConfirmationConfig = async (exportConfirmationConfigUpdate: Partial<ExportConfirmationConfig>) => {
    if (!account) {
      return
    }
    try {
      await updateAccount({
        accountId,
        payload: {
          config: {
            ...account.data.config,
            exportConfirmation: {
              ...account.data.config.exportConfirmation,
              ...exportConfirmationConfigUpdate,
            },
          },
        },
      })
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  const onSubmit = async (formValues: ExportConfirmationSettingsFormValues) => {
    const { message } = formValues
    if (!message) {
      return
    }

    await updateExportConfirmationConfig({ message })
  }

  return (
    <Container fluid className='d-flex flex-column gap-3'>
      <Row>
        <Col className='p-0'>
          <div className='d-flex align-items-center gap-3 zocks-p-1.25 zocks-bg-color-primary-light rounded'>
            <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
            <span className='zocks-text-gray-900'>
              Confirmation dialogs can optionally be enforced when user save data to third-party systems (CRM, Financial Planning, etc.). The dialogs require
              acknowledgment before proceeding and display the custom message configured below.
            </span>
          </div>
        </Col>
      </Row>
      {exportConfirmationIsEnforcedByEnterpriseConfig ? (
        <Row>
          <Col className='p-0'>
            <div className='d-flex align-items-center gap-3 zocks-p-1.25 zocks-bg-color-primary-light rounded'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>Confirmation Notice on Data Exports settings are managed at the enterprise level.</span>
            </div>
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col className='p-0'>
          <h6>I want to enforce data export confirmations when</h6>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <div className='d-flex align-items-center gap-2'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Check
                id='export-confirmation-config-integration-enabled'
                className='d-flex align-items-center justify-content-center'
                disabled={!account || !exportConfirmationEnabled || exportConfirmationIsEnforcedByEnterpriseConfig || isAccountFetching || accountUpdating}
                onChange={async (e) => await updateExportConfirmationConfig({ integrationEnabled: e.target.checked })}
                checked={integrationEnabled}
              />
            </ScopedBlock>
            <label htmlFor='export-confirmation-config-integration-enabled' className='paragraph-medium'>
              Saving data to external systems (CRM, Financial Planning, etc.)
            </label>
          </div>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <div className='d-flex align-items-center gap-2'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Check
                id='export-confirmation-config-document-enabled'
                className='d-flex align-items-center justify-content-center'
                disabled={!account || !exportConfirmationEnabled || exportConfirmationIsEnforcedByEnterpriseConfig || isAccountFetching || accountUpdating}
                onChange={async (e) => await updateExportConfirmationConfig({ documentEnabled: e.target.checked })}
                checked={documentEnabled}
              />
            </ScopedBlock>
            <label htmlFor='export-confirmation-config-document-enabled' className='paragraph-medium'>
              Exporting to documents (PDF, CSV, etc.)
            </label>
          </div>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <h6>Confirmation message</h6>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <Controller
            control={form.control}
            name='message'
            render={({ field }) => (
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Form.Control
                  disabled={
                    !account ||
                    !exportConfirmationEnabled ||
                    exportConfirmationIsEnforcedByEnterpriseConfig ||
                    isAccountFetching ||
                    accountUpdating ||
                    (!integrationEnabled && !documentEnabled)
                  }
                  as='textarea'
                  rows={3}
                  placeholder='Customize'
                  value={field.value}
                  onChange={field.onChange}
                />
              </ScopedBlock>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ScopedBlock
            scope={{
              scope: 'account:update',
              superScope: ['account:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Button
              className='align-self-start'
              variant='primary'
              onClick={form.handleSubmit(onSubmit)}
              disabled={
                !account ||
                !exportConfirmationEnabled ||
                exportConfirmationIsEnforcedByEnterpriseConfig ||
                isAccountFetching ||
                accountUpdating ||
                !form.formState.isDirty
              }
            >
              Save changes
            </Button>
          </ScopedBlock>
        </Col>
      </Row>
    </Container>
  )
}
